import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useLanguage } from '../i18n/LanguageContext';
import SignaturePad from './SignaturePad';

export default function BadgeCheckinModal({ onClose }) {
  const { t } = useLanguage();
  const [mode, setMode] = useState('nfc'); // 'nfc' | 'code'
  const [scanValue, setScanValue] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle'); // idle | looking-up | awaiting-signature | choosing-reference | submitting | success | error
  const [message, setMessage] = useState('');
  const [pendingUser, setPendingUser] = useState(null); // { externalUserId, badgeIdentifier, nome, cognome, ditta, references }
  const [signatureData, setSignatureData] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  const resetAll = () => {
    setStatus('idle');
    setMessage('');
    setPendingUser(null);
    setSignatureData(null);
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setScanValue('');
    setCode('');
    resetAll();
  };

  const finishCheckin = async (externalUserId, badgeIdentifier, signature, referenceContactId) => {
    setStatus('submitting');
    setMessage('');
    try {
      const res = await api.externalCheckinConfirm({
        externalUserId,
        badgeIdentifier,
        signature_data: signature || undefined,
        referenceContactId: referenceContactId || undefined,
      });
      setStatus('success');
      setMessage(t('welcomeBack')(res.nome, res.cognome));
      setTimeout(onClose, 1800);
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
      setScanValue('');
      setCode('');
      setPendingUser(null);
      setSignatureData(null);
      setTimeout(() => {
        setStatus('idle');
        inputRef.current?.focus();
      }, 2200);
    }
  };

  // Dopo la firma (o subito, se non richiesta): se la persona ha più di una
  // persona di riferimento collegata, le viene chiesto di scegliere; con
  // una sola o nessuna, si procede dritti alla conferma.
  const proceedAfterSignature = (user, signature) => {
    if (user.references && user.references.length > 1) {
      setStatus('choosing-reference');
    } else {
      finishCheckin(user.externalUserId, user.badgeIdentifier, signature, null);
    }
  };

  const startCheckin = async (payload) => {
    setStatus('looking-up');
    setMessage('');
    try {
      const res = await api.externalCheckinLookup(payload);
      const user = {
        externalUserId: res.externalUserId,
        badgeIdentifier: res.badgeIdentifier,
        nome: res.nome,
        cognome: res.cognome,
        ditta: res.ditta,
        references: res.references || [],
      };
      setPendingUser(user);
      if (res.signatureRequired) {
        setStatus('awaiting-signature');
      } else {
        proceedAfterSignature(user, null);
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
      setScanValue('');
      setCode('');
      setTimeout(() => {
        setStatus('idle');
        inputRef.current?.focus();
      }, 2200);
    }
  };

  const handleNfcKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (scanValue.trim()) startCheckin({ nfc_uid: scanValue.trim() });
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) startCheckin({ unique_code: code.trim() });
  };

  const handleConfirmSignature = () => {
    if (!signatureData || !pendingUser) return;
    proceedAfterSignature(pendingUser, signatureData);
  };

  const handleChooseReference = (referenceContactId) => {
    if (!pendingUser) return;
    finishCheckin(pendingUser.externalUserId, pendingUser.badgeIdentifier, signatureData, referenceContactId);
  };

  const handleCancelSignature = () => {
    resetAll();
    setScanValue('');
    setCode('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className="modal-overlay" onClick={status === 'awaiting-signature' || status === 'choosing-reference' ? undefined : onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h2>{t('checkinTitle')}</h2>

        {status === 'success' ? (
          <div className="form-success">
            <div className="success-icon">✓</div>
            <p className="modal-subtitle">{message}</p>
          </div>
        ) : status === 'awaiting-signature' && pendingUser ? (
          <>
            <p className="modal-subtitle">
              {t('badgeSignaturePrompt')(pendingUser.nome, pendingUser.cognome)}
            </p>
            <SignaturePad
              label={t('fieldSignature')}
              clearLabel={t('signatureClear')}
              hintText={t('signatureHint')}
              onChange={setSignatureData}
            />
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-ghost" onClick={handleCancelSignature}>{t('cancel')}</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmSignature}
                disabled={!signatureData || status === 'submitting'}
              >
                {status === 'submitting' ? '…' : t('confirm')}
              </button>
            </div>
          </>
        ) : status === 'choosing-reference' && pendingUser ? (
          <>
            <p className="modal-subtitle">{t('chooseReferencePrompt')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingUser.references.map((ref) => (
                <button
                  key={ref.id}
                  type="button"
                  className="btn btn-ghost"
                  disabled={status === 'submitting'}
                  onClick={() => handleChooseReference(ref.id)}
                  style={{ justifyContent: 'flex-start' }}
                >
                  {ref.nome} {ref.cognome}
                </button>
              ))}
              <button
                type="button"
                className="btn btn-ghost"
                disabled={status === 'submitting'}
                onClick={() => handleChooseReference(null)}
                style={{ justifyContent: 'flex-start', color: 'var(--muted-text)' }}
              >
                {t('chooseReferenceNone')}
              </button>
            </div>
          </>
        ) : mode === 'nfc' ? (
          <>
            <p className="modal-subtitle">{t('nfcInstruction')}</p>
            {status === 'error' && <div className="form-error">{message}</div>}
            <div className="field">
              <input
                ref={inputRef}
                type="text"
                className="big-badge-input"
                value={scanValue}
                onChange={(e) => setScanValue(e.target.value)}
                onKeyDown={handleNfcKeyDown}
                onBlur={() => mode === 'nfc' && status === 'idle' && inputRef.current?.focus()}
                placeholder={t('nfcPlaceholder')}
                disabled={status === 'looking-up' || status === 'submitting'}
                autoComplete="off"
              />
            </div>
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('cancel')}</button>
            <button type="button" className="doc-link-btn" style={{ display: 'block', margin: '14px auto 0' }} onClick={() => switchMode('code')}>
              {t('useCodeLink')}
            </button>
          </>
        ) : (
          <>
            <p className="modal-subtitle">{t('codeInstruction')}</p>
            {status === 'error' && <div className="form-error">{message}</div>}
            <form onSubmit={handleCodeSubmit}>
              <div className="field">
                <input
                  ref={inputRef}
                  type="text"
                  className="big-badge-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t('codePlaceholder')}
                  disabled={status === 'looking-up' || status === 'submitting'}
                  autoComplete="off"
                />
              </div>
              <div className="btn-row">
                <button type="button" className="btn btn-ghost" onClick={onClose}>{t('cancel')}</button>
                <button type="submit" className="btn btn-primary" disabled={status === 'looking-up' || status === 'submitting'}>
                  {status === 'looking-up' ? '…' : t('confirm')}
                </button>
              </div>
            </form>
            <button type="button" className="doc-link-btn" style={{ display: 'block', margin: '14px auto 0' }} onClick={() => switchMode('nfc')}>
              {t('useNfcLink')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
